import {UI, UIElement, type UIChild} from "../UIBase";
import {type Constructor} from "../../base/Utils";
import {Table, type TableOptions} from "./Table";
import {defaultComparator} from "../../base/Utils";
import {MakeIcon} from "../SimpleElements";
import {styleRule, StyleSheet, type StyleRules} from "../Style";
import {NodeAttributes} from "../NodeAttributes";
import {ColumnHandler} from "../../base/ColumnHandler";


export class SortableTableStyle extends StyleSheet {
    @styleRule
    container = {
        [" th:hover ." + this.sortIcon.getClassName()]: {
            visibility: "inherit",
        },
        " th:hover": {
            cursor: "pointer",
        }
    };

    @styleRule
    sortIcon = {
        position: "absolute",
        right: 0,
        bottom: 0,
        visibility: "hidden",
        float: "right",
    };
}

export interface SortableTableOptions<BaseType> extends TableOptions<BaseType> {
    columnSortingOrder?: ColumnHandler<BaseType>[];
}

export function SortableTableInterface<BaseType, T extends Constructor<Table<BaseType>>>(BaseTableClass: T) {
    class SortableTable extends BaseTableClass {
        sortBy?: ColumnHandler<BaseType>;
        sortDescending?: boolean;
        columnSortingOrder: ColumnHandler<BaseType>[] = [];
        getSortableStyleSheet(): StyleRules<SortableTableStyle> {
            return SortableTableStyle.getInstance(this.getTheme()); // Make this optional maybe
        }

        extraNodeAttributes(attr: NodeAttributes): void {
            super.extraNodeAttributes(attr);
            attr.addClass(this.getSortableStyleSheet().container);
        }

        setOptions(options: SortableTableOptions<BaseType>): void {
            super.setOptions(options);

            this.columnSortingOrder = options.columnSortingOrder || [];
        }

        renderColumnHeader(column: ColumnHandler<BaseType>): UIChild {
            if (column.noSort) {
                return super.renderColumnHeader(column);
            }
            const sortableStyleSheet = this.getSortableStyleSheet(); // TODO: use properly

            let sortIcon = <span style={{opacity: 0.4}}>{MakeIcon("sort")}</span>;
            if (this.isSortedBy(column)) {
                if (this.sortDescending) {
                    sortIcon = MakeIcon("sort-desc");
                } else {
                    sortIcon = MakeIcon("sort-asc");
                }
            }

            const reorderCallback = () => {
                this.sortByColumn(column);
                this.dispatch("reorder");
            }

            return <div style={{position: "relative"}} onClick={reorderCallback}>
                {super.renderColumnHeader(column)} {sortIcon}
            </div>;
        }

        // A table may rebuild its columns on every render, so the sorted one is recognised by its position
        isSortedBy(column: ColumnHandler<BaseType>): boolean {
            return this.sortBy?.index === column.index;
        }

        sortByColumn(column: ColumnHandler<BaseType>): void {
            if (this.isSortedBy(column)) {
                this.sortDescending = (this.sortDescending != true);
            } else {
                this.sortDescending = column.sortDescending ?? true; // A column that states no direction starts descending
            }

            this.sortBy = column;

            this.redraw();
        }

        getComparator(): ((a: BaseType, b: BaseType) => number) | null {
            if (!this.sortBy && this.columnSortingOrder.length === 0) {
                return null;
            }

            const colCmp = (a: BaseType, b: BaseType, column: ColumnHandler<BaseType>, sortDescending?: boolean): number => {
                if (!column) {
                    return 0;
                }

                const keyA = column.rawValue ? column.rawValue(a) : column.value(a);
                const keyB = column.rawValue ? column.rawValue(b) : column.value(b);

                const comparator = column.cmp || defaultComparator;
                const result = comparator(keyA, keyB);
                return sortDescending ? -result : result;
            };

            return (a: BaseType, b: BaseType) => {
                if (this.sortBy) {
                    const cmpRes = colCmp(a, b, this.sortBy, this.sortDescending);
                    if (cmpRes) {
                        return cmpRes;
                    }
                }

                for (const column of this.columnSortingOrder) {
                    const cmpRes = colCmp(a, b, column, column.sortDescending);

                    if (cmpRes) {
                        return cmpRes;
                    }
                }
                return 0;
            }
        }

        sortEntries(entries: BaseType[]): BaseType[] {
            let sortedEntries = entries.slice();

            const comparator = this.getComparator();
            if (comparator) {
                sortedEntries.sort(comparator);
            }

            return sortedEntries;
        }

        getEntries(): BaseType[] {
            return this.sortEntries(super.getEntries());
        }
    }
    return SortableTable;
}

// Named, not bound: a second binding would reach the emitted file
type SortableTableClass = ReturnType<typeof SortableTableInterface<any, Constructor<Table<any>>>>;

// The row type replaces the members that carry it
export type SortableTableOf<BaseType> = Omit<InstanceType<SortableTableClass>, keyof Table<BaseType>> & Table<BaseType>;

// The same name in type position, for a subclass's options
export type SortableTable<BaseType = any> = SortableTableOf<BaseType>;

// One shared class, with a generic construct signature so a subclass can name its row
export const SortableTable = SortableTableInterface(Table<any>) as unknown as Omit<SortableTableClass, "prototype"> & {
    new <BaseType = any>(options?: SortableTableOptions<BaseType>): SortableTableOf<BaseType>;
};
